import {appFetch} from "../../utils/appFetch";

export interface AppFetchResponse {
    data: any;
    status: number;
}

export type EventAggregation = "exists" | "count";
export type ExistsOperator = "EXISTS" | "NOT_EXISTS";
export type CountOperator = ">=" | ">" | "<=" | "<" | "=" | "!=";
export type EventOperator = ExistsOperator | CountOperator;

function unwrap<T>(res: AppFetchResponse): { ok: boolean; data?: T; error?: string } {
    const ok = res.status >= 200 && res.status < 300;
    const payload = res?.data;
    if (payload && typeof payload === "object" && "data" in payload) {
        return { ok, data: (payload as any).data as T, error: (payload as any).error };
    }
    return { ok, data: payload as T, error: (payload as any)?.error };
}

export type ConditionBundleDto = {
    condition: any;
    items: any[];
    basicById: Record<string, any>;
};

export const GetConditionBundle = async (domain: string, conditionId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/conditions/${conditionId}`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });
    return unwrap<ConditionBundleDto>(res);
};

export const PatchCondition = async (
    domain: string,
    conditionId: number,
    patch: Partial<{ title: string | null; operator: "AND" | "OR"; timeframe_seconds: number | null }>
) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/conditions/${conditionId}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        domain,
        params: patch,
    });
    return unwrap<{ ok: true }>(res);
};

export const CreateEventLeaf = async (
    domain: string,
    conditionId: number,
    body: {
        event_name: string;
        aggregation?: "exists" | "count";
        operator?: "EXISTS" | "NOT_EXISTS" | ">=" | ">" | "<=" | "<" | "=" | "!=";
        threshold?: number | null;
        sort_order?: number;
    }
) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/conditions/${conditionId}/event-leaf`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: body,
    } as any);

    return unwrap<{ basicId: number; itemId: number }>(res);
};

export const PatchBasicCondition = async (
    domain: string,
    basicId: number,
    patch: Partial<{
        title: string | null;
        event_name: string | null;
        aggregation: "exists" | "count";
        operator: "EXISTS" | "NOT_EXISTS" | ">=" | ">" | "<=" | "<" | "=" | "!=";
        threshold: number | null;
        filters_json: any | null;
    }>
) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/basic-conditions/${basicId}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        domain,
        params: patch,
    } as any);

    return unwrap<{ ok: true }>(res);
};

export const DeleteConditionItem = async (domain: string, itemId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/condition-items/${itemId}`,
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        domain,
    });
    return unwrap<{ ok: true }>(res);
};
